import type { Resource } from "i18next";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { getTranslations } from "@/services/translations";
import { isTranslationDbEnabled } from "@/store/adminSlice";
import { ImpactTargets } from "@/features/ImpactTargets/components/ImpactTargets";
import ProgramImpact from "@/Components/Widgets/ProgramImpact";
import PipelinePriority from "@/features/PipelinePriority/PipelinePriority";

const resources: Resource = {
  en: {
    translation: {
      brand: "MasterCard",
      home: "Home",
      about: "About",
      contact: "Contact",
      dashboard: "Dashboard",
      ideas: "Ideation Tracker",
      concepts: "Concepts",
      proposals: "Proposals",
      learningHub: "Learning Hub",
      disbursement: "Disbursement",
      storage: "Storage",
      scheduler: "Scheduler",
      users: "Users",
      comments: "Comments",
      ideasStatistics: "Ideas Statistics",
      questionnaire: "Questionnaire",
      detailedQuestionnaire: "Detailed Organisational Questionnaire",
      highLevelQuestionnaire: "High level Organisational Questionnaire",
      documentServices: "Document Services",
      menu: "Menu",
      noNotifications: "No notifications",
      unreadCount: "Unread",
      searchPlaceholder: "Search...",
      welcome: "Welcome to your dashboard",
      description: "Manage widgets",
      addWidget: "Add Widget",
      restartTour: "Restart Tour",
      toggleLayout: "Toggle Layout",
      language: "Language",
      widgetsTitle: "Widgets",
      emptyWidgets: "No widgets yet — add one!",
      roleSelectorLabel: "Select role",
      signIn: "Sign in",
      signOut: "Sign out",
      logout: "Log out",
      settings: "Settings",
      guest: "Guest",
      brandLabel: "Brand",
      brandAlt: "Logo",
      primaryNav: "Primary",
      profileMenu: "Profile menu",
      search: "Search",
      viewApplication: "View application as",
      opensInNewTab:"Opens in a new tab",
      viewProfile: {
        title: "View profile",
        header: "Profile Settings",
        subtitle: "Manage your personal information and account details",
        information: "Profile Information",
        name: "Name",
        email: "Email",
        organization: "Organization",
        role: "Role",
        close: "Close profile settings",
        languageOption: "Switch dashboard language to {{lang}}",
      },
      themes: {
        title: "Themes",
        vibrant: "Vibrant Theme",
        light: "Light Theme",
        dark: "Dark Theme",
      },
      accessibilityTitle: "Accessibility",

      timezoneBar: {
        officeHoursNow: "Office hours",
        officeHoursClosed: "Outside office hours",
        am: "AM",
        pm: "PM",
        settings: "Settings",
        save: "Save",
        cancel: "Cancel",
        moreLocationsOptions: "More locations options"
      },
      tooltip: {
       activeProgramsTitle : "Latest 20 records",
       impactTargetsTitle : "Data Refresh Information",
       oversightTitle : "Data Refresh Information",
       ideaTrackerTitle : "Data Refresh Information",
       programImpactTitle : "Data Refresh Information",
       analyticsWidgetTitle : "Data Refresh Information"
      },
      noAccess: {
        title: "Access denied",
        description:
          "You don’t have permission to access this page. Please contact your administrator or sign in with a different account.",
        returnToLogin: "Return to Login",
        returnToLoginAria: "Return to login and clear session",
      },
      feedbackForm: {
        title : "Ask for Help",
        categoryLabel: "Category",
        descriptionLabel: "Description",
        dropdownLabel: "Select Category",
        descriptionPlaceholder: "Describe your ask...",
        submitButton: "Submit",
        cancelButton: "Cancel",
        successMessage: "Thank you for Submission, We will get back in 48hrs",
        errorMessage: "Failed to submit, Please try again.",
        categoryError: "Please select a category.",
        descriptionError: "Please enter a description.",
        submitting: "Submitting..."
      },

      widgetSidebarConfig: {
        ProgramImpact: "Program Impact",
        IdeaTracker: "Idea Tracker",
        QuickAccessApps: "Quick Access Applications",
        ActivePrograms: "Active Programs",
        TaskPriorities: "Task Priorities",
        Notifications: "Workflow Notification",
        KeyGuidelines: "Key Guidelines",
        UpcomingMeetings: "Upcoming Meetings",
        FinanceTargets: "Financials",
        TeamCalendar: "Team Calendar",
        PartnersDirectory: "Partner Directory",
        LearningInsights: "Learning Insights",
        FinancialAnalytics: "Analytics",
        ProgramResources: "Resources",
        Oversight: "Oversight",
        MySubmissions: "My Submissions",
        PartnerSubmissions: "Partner Submissions",
        DisbursementRequests: "Disbursement Requests",

        WorldClocks: "World Clocks",
        AccountSettings: "Account Settings",
        Accessibility: "Accessibility",
        Terms: "Terms & Conditions",
      },
      widgetSidebarWidgetsSubtitles: {
        QuickAccessApps: "Access your frequently used tools",
        ActivePrograms: "Track your programs",
        TaskPriorities: "Focus on key tasks",
        Notifications: "Stay updated on your programs",
        KeyGuidelines: "Key information and policies",
        UpcomingMeetings: "Scheduled meetings",
        FinanceTargets: "Track performance targets",
        TeamCalendar: "View team schedule",
        PartnersDirectory: "Partner directory",
        LearningInsights: "Learning recommendations",
        FinancialAnalytics: "Insights and trends",
        ProgramResources: "Key resources",
        Oversight: "Program management system",
        MySubmissions: "Manage submissions",
        PartnerSubmissions: "Manage submissions",
        DisbursementRequests: "Financial requests",

        WorldClocks: "Global team time zones",
        AccountSettings: "Manage your account and preferences",
        Accessibility: "Adjust accessibility settings",
        Terms: "Review terms and conditions",
        IdeaTracker: "Track ideas and their progress",
      },

      components: {
        preferencesButton: {
          buttonLabel: "Set Preferences",
          dialogTitle: "User Preferences",
          roleLabel: "Role",
          timeZoneLabel: "Time Zone",
          workingHoursTitle: "Working Hours",
          workingHoursTip: "Tip: Ensure end time is after start time for enabled days.",
          blackoutDatesTitle: "Blackout Dates",
          addRange: "+ Add Range",
          noBlackouts: "No blackout ranges added yet.",
          startDate: "Start date",
          endDate: "End date",
          noteOptional: "Note (optional)",
          notePlaceholder: "e.g., Public holiday",
          remove: "Remove",
          cancel: "Cancel",
          save: "Save",
          ariaEnableDay: "Enable {{day}}",
          validateHours: "Please ensure for each enabled day: End time is after Start time.",
          validateBlackout: "Blackout: End date must be after or equal to Start date.",
        },
        externalLinks: {
          viewInOutlook: "View in Outlook",
          viewInOutlookAria: "Open Outlook calendar in a new tab",
        },
        tour: {
          timezoneBar: "This bar shows current times in key locations across Africa.",
          searchIcon: "Click here to access the search functionality.",
          chatIcon: "Click here to access the chat sidebar for team communication.",
          languageSwitcher: "Use this menu to change the dashboard language.",
          notificationIcon: "View your notifications here.",
          profileMenu: "Use this menu to manage your profile and settings.",
          finish: "Finish",
          skip: "Skip",
          next: "Next",
          previous: "Previous",
        },
        notifications: {
          noDataAvailable: "No notifications available.",
        },
        dashboardLayoutSettings: {
          dashboardLayout: "Dashboard Layout",
          dashboardLayoutDescription: "Customize which widgets appear on your dashboard.",
          coreWidgets: "CORE WIDGETS",
          coreWidgetsDescription: "These widgets are required and cannot be removed.",
          optionalWidgets: "OPTIONAL WIDGETS",
          optionalWidgetsDescription: "Changes are saved automatically for this user.",
        },
        dashboard: {
          welcomeMessage: "Welcome, ",
          QuickActions: "Quick Actions",
          noAccessTitle: "Access Not Available",
          noAccessMessage: " You are not currently mapped as a Daraja user. If you require access to perform programming tasks, please request access. The relevant teams will review and assess your request.",
          noAccessRequestButton : "Request Access",
          noAccessSignOutButton: "Sign Out"
        },
        NewIdeaHeader: {
          newIdea: {
            title: "Create New Idea",
            subtitle: "Assign users and upload supporting documentation",
          },
        },
        newIdeaForm: {
          progress: "Step {{step}} of {{total}}",
          formLabel: "New Idea form",
          ideaName: {
            label: "Idea/Program Name",
            placeholder: "e.g., Digital Literacy Program for Rural Youth in East Africa",
          },
          characters: "characters",
          sourceOfIdea: { label: "Source of Idea" },
          startDate: { label: "Start Date" },
          endDate: { label: "End Date" },
          dateFormat: "Format: mm/dd/yyyy",
          platformThematic: { label: "Platform/Thematic" },
          estimatedBudget: { label: "Estimated Budget (USD)", placeholder: "0" },
          optional: "Optional",
          programSubgroup: { label: "Program Subgroup" },
          description: {
            label: "Description",
            placeholder: "Explain the program concept, activities, and intended impact",
          },
          characters_min: "characters (minimum {{min}})",
          prospectivePartner: { label: "Prospective Partner" },
          buttons: {
            cancel: "Cancel",
            saveDraft: "Save as Draft",
            continue: "Continue to Details",
            back: "Back",
            create: "Create Idea",
          },
          assignUser: {
            heading: "Assign User/s to Work Item",
            label: "User to Assign Idea To",
            selectUser: "Select a user",
          },
          documents: {
            heading: "Additional Documentation to Include",
            dropAreaAria: "File upload drop area",
            dropText: "Drag & drop files here, or click to browse",
            supports: "Supports single or bulk upload • PDF, DOC, XLS, PPT",
            browse: "Browse Files",
            chooseFilesAria: "Choose files to upload",
            remove: "Remove",
            removeFile: "Remove file {{name}}",
          },
          objectives: {
            label: "Key Objectives & Success Metrics",
            placeholder: "What specific outcomes do you aim to achieve",
          },
          background: {
            label: "Background & Rationale",
            placeholder: "What context, research, or evidence supports this idea?",
          },
          risks: {
            label: "Key Risks & Assumptions",
            placeholder: "What challenges or dependencies should we be aware of?",
          },
          tags: {
            label: "Tags",
            placeholder: "Add comma-separated keywords (e.g., Digital Skills, Youth, Rural)",
          },
        },

        widgets: {
          programImpact: {
            title: "Program Impact",
            powerBiTitle: "Open Power BI",
            youthInWork: "Youth-in-work",
            youngWomenInWork: "% Young Women-in-work",
            workEnablingOutreach: "Work-enabling Outreach",
            womenWorkEnablingOutreach: "% Women Work-enabling Outreach",
          },
          quickAccessApps: {
            title: "Quick Access Applications",
            description: "Your most frequently used applications for quick access.",

            addApp: "Add Application",
            edit: "Edit",
            done: "Done",
            deleteTitle: "Delete Application",
            deleteMessage: "Are you sure you want to delete",
            deleteWarning: "This action cannot be undone.",
            cancel: "Cancel",
            delete: "Delete",
            comingSoon: "Coming Soon",
            openInNewTab: "{{appName}} (opens in a new tab)",
          },

          programResources: {
            title: "Resources",
            loading: "Loading resources...",
            updated: "Updated",
            view: "View",
            viewAria: "View {{title}}",
            items: {
              programDevelopment: "Program Development Guide",
              elevStrategy: "The EleV Strategy",
              safeguarding: "Child and Youth Safeguarding Guidelines",
            },
          },
          partnerHighlights: {
            title: "Partner Lessons Learned and Highlights",
            description: "Real stories and insights from the field",
            loading: "Loading partner highlights...",
            error: "Failed to load partner highlights",
            viewAll: "View All Case Studies",
          },
          financeTargets: {
            title: "Financials",
            error: "Unexpected error loading finance targets",
            loading: "No data available for finance targets",
            labelFilter: "Foundation Wide:",
            filterText: "Select Platforms",
            filterLabel: "Select Platforms",
            powerBititle: "Open Power BI",
            headerTitle: "Active Program Metrics",
            actual: "Program Disbursement",
            target: "Program Total Commitment",
          },
          ideasTracker: {
            title: "Ideas Tracker",
            powerBiTitle: "Open Power BI",
            openPowerBIAria: "Open Power BI dashboard in a new tab",
            metrics: "Ideas tracker metrics",
            ideasSubmitted: "Ideas Submitted",
            underReview: "Under Review",
            onHold: "On Hold",
            rejected: "Rejected",
            converted: "Converted",
          },
          todaysPriorities: {
            loading: "Loading priorities…",
            error: "Failed to load priorities",
            today: "Today",
            week: "Week",
          },
          PaymentsPipelineWidget: {
            title: "Payments Pipeline",
          },
        },

        insights: {
          containerLabel: "Insights panel",
          tabsLabel: "Time range",
          tabs: {
            weekly: "Weekly",
            monthly: "Monthly",
            quarterly: "Quarterly",
          },
          sections: {
            timeSpent: "Time Spent on Tasks",
            kpis: "Key Performance Indicators",
            goals: "Goal Progress",
          },
          viewAnalytics: "View Detailed Analytics",
          activeRange: "Showing {{range}} insights",
          aria: {
            toggleSection: "Toggle {{section}} section",
          },
        },

        systemsSupport: {
          title: "Systems & Support",
          activeRequests: "Active Requests",
          needAssistanceTitle: "Need assistance?",
          needAssistanceBody: "Our support team is ready to help with any questions or issues.",
          contactSupport: "Contact Support",

          statuses: {
            pending: "Pending",
            inProgress: "In Progress",
            resolved: "Resolved",
          },

          aria: {
            containerLabel: "Systems and Support panel",
            listLabel: "Active requests list",
            requestItem: "Request",
            status: "Status",
            postedOn: "Posted on {{date}}",
            expandDetails: "Show details for {{title}}",
            collapseDetails: "Hide details for {{title}}",
            loaded: "Loaded {{count}} active requests",
            loading: "Loading active requests",
            error: "Failed to load requests",
            contactSupport: "Contact Support",
          },
        },

        turnaroundTimes: {
          title: "Turnaround Times",
          subtitle: "Project deadlines",
          dueInDays: "Due in {{count}} days",
          dueTomorrow: "Due tomorrow",
          dueToday: "Due today",
          severity: {
            critical: "Critical",
            high: "High",
            normal: "Normal",
          },
          aria: {
            containerLabel: "Turnaround Times panel",
            listLabel: "Deadlines list",
            task: "Task",
            project: "Project",
            due: "Due",
            severity: "Severity",
            detailsShow: "Show details for {{title}}",
            detailsHide: "Hide details for {{title}}",
            loading: "Loading deadlines",
            loaded: "Loaded {{count}} deadlines",
            error: "Failed to load deadlines",
            warning: "Warning: {{days}} days until overdue for project {{project}}",
          },
          warningTitle: "Warning: {{days}} Days Until Overdue",
          warningBody: 'Project "{{project}}" requires immediate attention',
        },

        commUpload: {
          title: "Communication Upload",

          labels: {
            selectFile: "Select Excel File",
            selected: "Selected:",
          },

          headers: {
            tag: "Tag",
            partnerOrg: "Partner Organization",
            ppp1: "PPP1",
            ppp2: "PPP2 (If Applicable)",
            fpp: "FPP",
            estQuarterOrig: "Estimated Quarter of Approval (Original)",
          },

          buttons: {
            downloadTemplate: "Download Template",
            downloadUploaded: "Download Uploaded",
            submit: "Excel to Table",
            submitToServer: "Submit to Server",
          },

          alerts: {
            templateMissing: "Template file missing. HTTP {{status}}",
            templateHtmlError:
              "Template path returned HTML instead of Excel. Check /public/templates folder.",
            templateDownloadFailed: "Could not download template.",
            uploadFirst: "Please upload a file first",
            dataSubmitted: "Data submitted successfully!",
            dataSubmitFailed: "Submission failed. {{message}}",
          },
        },

        ideasStats: {
          heading: "Ideas Statistics",
          periodLabel: "Period",
          periodQuarterly: "Quarterly",
          tabs: ["Q1", "Q2", "Q3", "Q4"],
          total: "Total",
          inProgress: "In Progress",
          progressedToConcept: "Progressed to Concept",
          notProgressed: "Not Progressed",
          loading: "Loading",
          error: "Something went wrong",
          announceQuarter: "Showing {{q}}",
          announcePeriod: "Period changed to {{p}}",
        },

        /* ===========================
           Active Programs (EN)
           =========================== */
        activePrograms: {
          title: "Active Programs",
          loadingPhase: "Loading {{phase}}",
          phaseCount: "{{phase}}, {{count}} programs",
          loadingCount: "Loading count",
          loading:"Loading active Prgrams",
          count: "{{count}} programs",
          refresh: "Refresh Active Programs",
          filters: {
            my: "My Programs",
            team: "My team",
            all: "All Programs",
          },
          selectPhase: "Select phase",
          actions: {
            addNew: "Add New Program",
          },
          empty: {
            noData: "No programs are available in this phase at the moment",
          },
          phases: {
            planning: "Program Planning & Pipeline",
            ideation: "Ideation",
            concept: "Concept",
            proposal: "Proposal Development",
            review: "Review & Approval",
            contracting: "Contracting",
            disbursement: "Disbursement",
            implementation: "Implementation & Monitoring",
            closeout: "Close-Out",
          },
          aria: {
            tabsLabel: "Active program phases",
            scrollLeft: "Scroll phases left",
            scrollRight: "Scroll phases right",
          },
        },

        PipelinePriority:{
          title : "Priority Pipeline",
          notFound: " No pipeline priorities found"
        },

        mySubmission: {
          title: "My Submissions",
          createdDate: "Created Date",
          amountRequested: "Amount Requested",
        },

        keyGuidelines: {
          title: "Key Guidelines",
          view: "View",
        },

        taskPriorities: {
          title: "Task Priorities",

          workflow: "From Workflow",

          due: "Due",

          overdue: "Overdue by {{count}} Days",

          buttons: {
            viewInFluxx: "View in Fluxx",
          },

          states: {
            loading: "Loading tasks…",
            error: "Something went wrong",
          },
          noData: "No tasks found",
          error: "Failed to load task priorities",
        },

        notificationTitle: "Workflow Notification",
        noDataNotifications: "No notifications available",
        errorNotifications: "Failed to load notifications",

        notificationColumn: "Notification",
        updatedDateColumn: "Updated date",

        programMetrics: {
          title: "Program Metrics",
          subtitle: "Track your program's performance",
          viewInFluxx: "View in Fluxx",
          progressTitle: "Funding Distribution Timeline",
          Highlights: {
            title: "Stage bottlenecks — average days spent per stage",
            Subtitle: "Highlights where programs are taking longest to progress - last 12 months",
          },
        },
        analyticsInsights: {
          title: "Analytics & Insights",
          portfolioTitle: "Portfolio Performance",
          financialAnalytics: "Analytics",
          subtitle: "Portfolio performance via Power BI",
          powerBititle: "Open Power BI",
          progressTitle: "Funding Distribution Timeline",
          headertitle: "Programs",
          headerAlltitle: "All Programs",
          foundationWide: "Foundation wide:",
          filtertitle: "Foundation Wide",
          filterLabel: "Select Programs",
          activePrograms: "Active Programs",
          activeProgramsDescription: "Programs currently in any active stage",
          approvalTurnaround: "Average Approval Turnaround",
          approvalTurnaroundDescription: "Average days from submission to approval",
          disbursementOntime: " Disbursement on Time",
          disbursementOntimeDescription: "Tracks workflow completion with full disbursement rate",
          ApprovedPartners: "Approved Partners",
          ApprovedPartnersDescription: "Unique partner organisations with active records",
          avgTimeInStage: "Average time in stage",
          concept: "Concept",
          proposalDevelopment: "Proposal Development",
          reviewApprovals: "Review & Approvals",
          contracting: "Contracting",
          disbursement: "Disbursement",
          Highlights: {
            title: "Pipeline Trend Summary",
            Subtitle: "Highlights where programs are taking longest to progress · last 12 months",
          },
          errorMessage: "Failed to load financial insights",
          noData: "No data available for financial insights",
          filterText: "Select Platforms",
        },
        learningInsights: {
          title: "Learning & Insights",
          loading: "Loading insights…",
          noInsights: "No insights available for this category.",
          updated: "Updated",
          readMore: "Read more",
          comingSoon: "Coming Soon",
          tabs: {
            lessonsLearned: "Lessons Learned",
            skillBuildingTip: "Skill-Building Tip",
          },
          errors: {
            failedToLoad: "Failed to load Learning Insights",
            unexpected: "Unexpected error",
          },
        },
        accessibilityPanel: {
          title: "Accessibility Settings",

          aria: {
            close: "Close accessibility panel",
          },

          header: {
            title: "Accessibility Settings",
            description: "Customize your experience to meet your accessibility needs.",
            mobdescription: "Customize your experience.",
          },

          sections: {
            cursorSize: {
              title: "Cursor Size",
              description: "Adjust pointer size for better visibility",
            },

            interfaceScaling: {
              title: "Interface Scaling",
              description: "Adjust zoom level to make content larger or smaller",
              zoomLabel: "Zoom Level",
            },
          },

          groups: {
            voiceSpeech: {
              title: "Voice & Speech",
              description: "Audio feedback and voice control",
            },

            screenReaderCognitive: {
              title: "Screen Reader & Cognitive",
              description: "Specialized reading and cognitive support",
            },

            visualEnhancements: {
              title: "Visual Enhancements",
              description: "Adjust visual elements for better visibility",
            },

            motionAnimation: {
              title: "Motion & Animation",
              description: "Control animations and motion effects",
            },

            keyboardNavigation: {
              title: "Keyboard Navigation",
              description: "Enhanced keyboard accessibility support",
            },
          },

          toggles: {
            textToSpeech: {
              label: "Text to Speech",
              description: "Read screen content aloud",
            },

            readOnFocus: {
              label: "Read on Focus",
              description: "Automatically read elements when focused",
            },

            readOnHover: {
              label: "Read on Hover",
              description: "Automatically read elements on hover",
            },
            screenReaderOptimization: {
              label: "Screen Reader Optimization",
              description: "Enhanced ARIA labels and announcements",
            },

            dyslexiaFriendlyFont: {
              label: "Dyslexia-Friendly Font",
              description: "Use easier-to-read font for dyslexia",
            },

            highContrast: {
              label: "High Contrast Mode",
              description: "Increase contrast for better visibility",
            },

            underlineLinks: {
              label: "Underline Links",
              description: "Make links visually distinguishable",
            },

            increaseTextSpacing: {
              label: "Increase Text Spacing",
              description: "Add extra spacing for improved readability",
            },

            reduceMotion: {
              label: "Reduce Motion",
              description: "Minimize animations and motion effects",
            },

            enhancedKeyboardNav: {
              label: "Enhanced Keyboard Navigation",
              description: "Improve keyboard-only navigation",
            },

            enhancedFocusIndicators: {
              label: "Enhanced Focus Indicators",
              description: "Make focused elements easier to see",
            },
          },
          speech: {
            speechRate: "Speech Rate",
            enabledAnnouncement: "Text to speech has been enabled.",
          },

          cursorOptions: {
            normal: "Normal",
            large: "Large",
            xlarge: "Extra Large",
          },

          buttons: {
            resetAll: "Reset All",
            applySettings: "Apply Settings",
          },
        },
        partnerDirectory: {
          title: "Partner Directory",
          searchPlaceholder: "Search partner",
          country: "Select Country",
          getDetails: "Show",
          noResults: "No partners are available for the searched criteria",

          columns: {
            organization: "Organization Details",
            email: "Organization Email",
            country: "Country",
            contact: "Primary Contact",
            status: "Status",
          },

          buttons: {
            viewAll: "View in Fluxx",
          },

          states: {
            noData: "No partners found",
          },
        },

        sessionTimeout: {
          title: "Session timeout warning",

          description:
            "You’ve been inactive for a while. For your security, we will sign you out automatically unless you remain active.",

          countdown: "Auto logout in {{time}}",

          logout: "Logout now",
          stay: "Stay signed in",
        },

        MeetingsWidget: {
          title: "Upcoming Meetings",
          noMeetings: "No upcoming meetings.",
          loading: "Loading meetings…",
          synced: "📍 Synced from Outlook · program-related only",
          schedule: "+ Schedule meeting",
          filters: {
            all: "All",
            partner: "Partner meetings",
            internal: "Internal reviews",
            workshop: "Workshops",
          },
          viewInOutlook: "View in Outlook",
          startingSoon: "Starting Soon",
          minsMeeting: "30 Mins",
          joinMeeting: "Join Meeting",
        },
        teamCalendarWidget: {
          title: "Team Calendar",
          loading: "Loading calendar…",
          noEvents: "No upcoming events",
        },

        AnalyticsWidget: {
          name: "Analytics",
          loading: "Loading analytics…",
        },

        SubmissionsWidget: {
          title: "My Submissions",
          activeCount: "Active",
          description: "Track all your concept notes, proposals, and reports",
          loading: "Loading submissions...",
          empty: "No submissions",
          tabs: {
            upcoming: "Upcoming",
            completed: "Completed",
          },
        },

        OversightWidget: {
          loading: "Loading oversight data...",
          lastSynced: "Last synced:",
          nextSync: "Next scheduled sync",
          capacity: "{{value}}% capacity",
          title: "Oversight",
          subtitle: "Program management system",
          status: "Open",
          activePrograms: "Active Programs",
          pendingReviews: "Pending Reviews",
          SeverelyDelayed: "Severely Delayed",
          workFlowCompletion: "Workflow Completion Rate",
          powerBititle: "Open Power BI",
          error: "Unexpected error loading oversight data",
          noData: "No data available for oversight",
        },
        navBar: {
          sidebarItems: {
            dashboard: "Dashboard",
            dashboardSettings: "Dashboard Settings",
            programPlanning: "Program Planning & Pipeline",
            ideationTracker: "Ideation Tracker",
            concepts: "Concepts",
            proposals: "Proposals",
            comments: "Comments",
            documentServices: "Document Services",
            learningHub: "Learning Hub",
            Menu: "Menu",
            worldClocks: "World Clocks",
            accountSettings: "Account Settings",
            accessibility: "Accessibility",
            accessibilitySettings: "Accessibility Settings",
            privacyPolicy: "Privacy Policy",
            terms: "Terms & Conditions",
            safeguarding: "Safeguarding",
          },

          header: {
            toggleMenu: "Toggle menu",
            guest: "Guest",
            expandNavigationMenu: "Expand navigation menu",
            collapseNavigationMenu: "Collapse navigation menu",
          },
          tour: "Take a Tour",
          notifications: "Notifications",
          language: "Languages",
        },

        profileMenu: {
          preferences: "Preferences",
          theme: "THEME",
          theme1: "Theme 1 (Orange Header)",
          theme2: "Theme 2 (Light Header)",
          themeDark: "Dark Theme",
          accessibility: "Accessibility",
          accessibilitySettings: "Accessibility Settings",
          signOut: "Sign out",
          signIn: "Sign in",
        },

        footer: {
          copyright: "© Mastercard Foundation 2026",
          message: "This dashboard meets WCAG 2.1 Level AA standards. For assistance, contact ",
          links: {
            privacyPolicy: "Privacy Policy",
            accessibility: "Accessibility",
            termsAndConditions: "Terms & Conditions",
            safeguarding: "Safeguarding",
          },
          logoAlt: "Daraja",
        },
        partnerSubmission: {
          title: "Partner Submissions",
          loading: "Loading submissions...",
          noSubmissions: "No submissions found.",
          viewInFluxx: "View in Fluxx",
          dueTitle: "Due:",
        },
        disbursementRequestsWrapper: {
          title: "Disbursement Requests",
          subtitle: "Pending requests requiring your review ",
          pendingCount: "{{count}} pending",
          totalAmount: "${{amount}} total",
          error: "Unexpected error loading disbursement requests",
          loading: "Loading disbursement requests...",
          reviewButton: "Review in Fluxx",
          viewInFluxx: "View in Fluxx",
          disbursementRequestsTitle: "Disbursement Requests Amount",
          disbursementPaidTitle: "Disbursement Amount Paid",
          noData: "No pending disbursement requests found",
        },
      },
    },
  },
  fr: {
    translation: {
      brand: "MasterCard",
      home: "Accueil",
      about: "À propos",
      contact: "Contact",
      dashboard: "Tableau de bord",
      ideas: "Idées",
      concepts: "Concepts",
      proposals: "Proposals",
      learningHub: "Centre d'apprentissage",
      disbursement: "Décaissement",
      storage: "Stockage",
      scheduler: "Planificateur",
      users: "Utilisateurs",
      comments: "Commentaires",
      ideasStatistics: "Statistiques des idées",
      questionnaire: "Questionnaire",
      detailedQuestionnaire: "Questionnaire organisationnel détaillé",
      highLevelQuestionnaire: "Questionnaire organisationnel de haut niveau",
      documentServices: "Services de documents",
      menu: "Menu",
      notifications: "Notifications",
      noNotifications: "Aucune notification",
      unreadCount: "Non lus",
      searchPlaceholder: "Rechercher...",
      welcome: "Bienvenue sur votre tableau de bord",
      description: "Gérez les widgets",
      addWidget: "Ajouter un widget",
      restartTour: "Redémarrer la visite",
      toggleLayout: "Changer la mise en page",
      language: "Langue",
      widgetsTitle: "Widgets",
      emptyWidgets: "Aucun widget pour le moment — ajoutez-en un !",
      roleSelectorLabel: "Sélectionner un rôle",
      signIn: "Se connecter",
      signOut: "Se déconnecter",
      logout: "Se déconnecter",
      settings: "Paramètres",
      guest: "Invité",
      brandLabel: "Marque",
      brandAlt: "Logo",
      primaryNav: "Principal",
      profileMenu: "Menu du profil",
      search: "Rechercher",
      opensInNewTab:"S’ouvre dans un nouvel onglet",
      viewApplication: "Afficher l'application en tant que",
      viewProfile: {
        title: "Afficher le profil",
        header: "Paramètres du profil",
        subtitle: "Gérez vos informations personnelles et les détails de votre compte",
        information: "Informations du profil",
        name: "Nom",
        email: "E-mail",
        organization: "Organisation",
        role: "Rôle",
        close: "Fermer les paramètres du profil",
        languageOption: "Changer la langue du tableau de bord en {{lang}}",
      },
      themes: {
        title: "Thèmes",
        vibrant: "Thème vibrant",
        light: "Thème clair",
        dark: "Thème sombre",
      },
      accessibilityTitle: "Accessibilité",

      timezoneBar: {
        officeHoursNow: "Heures de bureau",
        officeHoursClosed: "En dehors des heures de bureau",
        am: "AM",
        pm: "PM",
        settings: "Paramètres",
        save: "Enregistrer",
        cancel: "Annuler",
        moreLocationsOptions: "Plus d’options d’emplacements"
      },
      tooltip: {
  activeProgramsTitle: "20 derniers enregistrements",
  impactTargetsTitle: "Informations sur l’actualisation des données",
  oversightTitle: "Informations sur l’actualisation des données",
  ideaTrackerTitle: "Informations sur l’actualisation des données",
  programImpactTitle: "Informations sur l’actualisation des données",
  analyticsWidgetTitle: "Informations sur l’actualisation des données"
},
      noAccess: {
        title: "Accès refusé",
        description:
          "Vous n’avez pas la permission d’accéder à cette page. Veuillez contacter votre administrateur ou vous connecter avec un autre compte.",
        returnToLogin: "Retour à la connexion",
        returnToLoginAria: "Retour à la page de connexion et effacer la session",
      },
     feedbackForm: {
        title: "Demander de l'aide",
        categoryLabel: "Catégorie",
        descriptionLabel: "Description",
        dropdownLabel: "Sélectionner une catégorie",
        descriptionPlaceholder: "Veuillez fournir des détails sur votre demande d'assistance.",
        submitButton: "Soumettre",
        cancelButton: "Annuler",
        successMessage: "Merci pour votre soumission, nous vous répondrons dans les 48 heures.",
        errorMessage: "Échec de la soumission, veuillez réessayer.",
        categoryError: "Veuillez sélectionner une catégorie.",
        descriptionError: "Veuillez saisir une description.",
        submitting: "Soumission en cours...",
      },

      widgetSidebarConfig: {
        ProgramImpact: "Impact du programme",
        IdeaTracker: "Suivi des idées",
        QuickAccessApps: "Applications d'accès rapide",
        ActivePrograms: "Programmes actifs",
        TaskPriorities: "Priorités des tâches",
        Notifications: "Notifications du flux de travail",
        KeyGuidelines: "Lignes directrices clés",
        UpcomingMeetings: "Réunions à venir",
        FinanceTargets: "Finances",
        TeamCalendar: "Calendrier de l'équipe",
        PartnersDirectory: "Annuaire des partenaires",
        LearningInsights: "Insights d'apprentissage",
        FinancialAnalytics: "Analyse",
        ProgramResources: "Ressources",
        Oversight: "Supervision",
        MySubmissions: "Mes soumissions",
        PartnerSubmissions: "Soumissions des partenaires",
        DisbursementRequests: "Demandes de décaissement",

        WorldClocks: "Horloges mondiales",
        AccountSettings: "Paramètres du compte",
        Accessibility: "Accessibilité",
        Terms: "Conditions générales",
      },
      widgetSidebarWidgetsSubtitles: {
        QuickAccessApps: "Accédez à vos outils les plus utilisés",
        ActivePrograms: "Suivez vos programmes",
        TaskPriorities: "Concentrez-vous sur les tâches clés",
        Notifications: "Vos dernières mises à jour",
        KeyGuidelines: "Informations et politiques clés",
        UpcomingMeetings: "Réunions planifiées",
        FinanceTargets: "Suivre les objectifs de performance",
        TeamCalendar: "Voir le calendrier de l'équipe",
        PartnersDirectory: "Annuaire des partenaires",
        LearningInsights: "Recommandations d'apprentissage",
        FinancialAnalytics: "Analyses et tendances",
        ProgramResources: "Ressources principales",
        Oversight: "Suivi et contrôle",
        MySubmissions: "Gérer les soumissions",
        PartnerSubmissions: "Gérer les soumissions",
        DisbursementRequests: "Demandes financières",

        WorldClocks: "Voir les fuseaux horaires des équipes",
        AccountSettings: "Gérer votre compte et vos préférences",
        Accessibility: "Ajuster les paramètres d'accessibilité",
        Terms: "Consulter les conditions générales",
      },
      components: {
        mySubmission: {
          title: "Mes soumissions",
          createdDate: "Date de création",
          amountRequested: "Montant demandé",
        },

        externalLinks: {
          viewInOutlook: "Voir dans Outlook",
          viewInOutlookAria: "Ouvrir le calendrier Outlook dans un nouvel onglet",
        },


        preferencesButton: {
          buttonLabel: "Définir les préférences",
          dialogTitle: "Préférences utilisateur",
          roleLabel: "Rôle",
          timeZoneLabel: "Fuseau horaire",
          workingHoursTitle: "Heures de travail",
          workingHoursTip:
            "Astuce : l'heure de fin doit être après l'heure de début pour les jours activés.",
          blackoutDatesTitle: "Périodes d’indisponibilité",
          addRange: "+ Ajouter une plage",
          noBlackouts: "Aucune plage d’indisponibilité pour le moment.",
          startDate: "Date de début",
          endDate: "Date de fin",
          noteOptional: "Note (optionnelle)",
          notePlaceholder: "ex. : Jour férié",
          remove: "Supprimer",
          cancel: "Annuler",
          save: "Enregistrer",
          ariaEnableDay: "Activer {{day}}",
          validateHours:
            "Assurez‑vous que pour chaque jour activé, l’heure de fin est après l’heure de début.",
          validateBlackout:
            "Indisponibilité : la date de fin doit être postérieure ou égale à la date de début.",
        },
        accessibilityPanel: {
          title: "Paramètres d’accessibilité",

          aria: {
            close: "Fermer le panneau d’accessibilité",
          },

          header: {
            title: "Paramètres d’accessibilité",
            description: "Personnalisez votre expérience selon vos besoins d’accessibilité.",
            mobdescription: "Personnalisez votre expérience",
          },

          sections: {
            cursorSize: {
              title: "Taille du curseur",
              description: "Ajustez la taille du pointeur pour une meilleure visibilité",
            },

            interfaceScaling: {
              title: "Mise à l’échelle de l’interface",
              description: "Ajustez le niveau de zoom pour agrandir ou réduire le contenu",
              zoomLabel: "Niveau de zoom",
            },
          },

          groups: {
            voiceSpeech: {
              title: "Voix et parole",
              description: "Retour audio et contrôle vocal",
            },

            screenReaderCognitive: {
              title: "Lecteur d’écran et cognition",
              description: "Aide spécialisée pour la lecture et la cognition",
            },

            visualEnhancements: {
              title: "Améliorations visuelles",
              description: "Ajustez les éléments visuels pour une meilleure visibilité",
            },

            motionAnimation: {
              title: "Mouvement et animation",
              description: "Contrôlez les animations et les effets de mouvement",
            },

            keyboardNavigation: {
              title: "Navigation clavier",
              description: "Support amélioré pour la navigation au clavier",
            },
          },

          toggles: {
            textToSpeech: {
              label: "Synthèse vocale",
              description: "Lire le contenu de l’écran à voix haute",
            },

            readOnFocus: {
              label: "Lecture au focus",
              description: "Lire automatiquement les éléments au focus",
            },

            readOnHover: {
              label: "Lecture au survol",
              description: "Lire automatiquement les éléments au survol",
            },

            screenReaderOptimization: {
              label: "Optimisation du lecteur d’écran",
              description: "Amélioration des labels ARIA et des annonces",
            },

            dyslexiaFriendlyFont: {
              label: "Police adaptée à la dyslexie",
              description: "Utiliser une police plus lisible pour la dyslexie",
            },

            highContrast: {
              label: "Mode contraste élevé",
              description: "Augmenter le contraste pour une meilleure visibilité",
            },

            underlineLinks: {
              label: "Souligner les liens",
              description: "Rendre les liens plus visibles",
            },

            increaseTextSpacing: {
              label: "Augmenter l’espacement du texte",
              description: "Améliorer la lisibilité avec plus d’espacement",
            },

            reduceMotion: {
              label: "Réduire les animations",
              description: "Limiter les mouvements et animations",
            },

            enhancedKeyboardNav: {
              label: "Navigation clavier améliorée",
              description: "Améliorer la navigation uniquement au clavier",
            },

            enhancedFocusIndicators: {
              label: "Indicateurs de focus améliorés",
              description: "Rendre le focus plus visible",
            },
          },

          speech: {
            speechRate: "Vitesse de lecture",
            enabledAnnouncement: "La synthèse vocale est activée.",
          },

          cursorOptions: {
            normal: "Normal",
            large: "Grand",
            xlarge: "Très grand",
          },

          buttons: {
            resetAll: "Réinitialiser",
            applySettings: "Appliquer les paramètres",
          },
        },
        tour: {
          timezoneBar:
            "Cette barre affiche les heures actuelles dans les principaux endroits d'Afrique.",
          searchIcon: "Cliquez ici pour accéder à la fonctionnalité de recherche.",
          chatIcon:
            "Cliquez ici pour accéder à la barre latérale de chat pour la communication en équipe.",
          languageSwitcher: "Utilisez ce menu pour changer la langue du tableau de bord.",
          notificationIcon: "Voir vos notifications ici.",
          profileMenu: "Utilisez ce menu pour gérer votre profil et vos paramètres.",
          finish: "Terminer",
          skip: "Passer",
          next: "Suivant",
          previous: "Précédent",
        },
        notifications: {
          noDataAvailable: "Aucune notification disponible",
        },
        dashboardLayoutSettings: {
          dashboardLayout: "Disposition du tableau de bord",
          dashboardLayoutDescription:
            "Personnalisez les widgets qui apparaissent sur votre tableau de bord.",
          coreWidgets: "WIDGETS DE BASE",
          coreWidgetsDescription: "Ces widgets sont requis et ne peuvent pas être supprimés.",
          optionalWidgets: "WIDGETS OPTIONNELS",
          optionalWidgetsDescription:
            "Les modifications sont enregistrées automatiquement pour cet utilisateur.",
        },

       dashboard: {
  welcomeMessage: "Bienvenue, ",
  QuickActions: "Actions rapides",
  noAccessTitle: "Accès non disponible",
  noAccessMessage:
    "Vous n'êtes actuellement pas associé à un utilisateur Daraja. Si vous avez besoin d'un accès pour effectuer des tâches de programmation, veuillez demander un accès. Les équipes concernées examineront et évalueront votre demande.",
  noAccessRequestButton: "Demander l'accès",
  noAccessSignOutButton: "Se déconnecter"
},
        widgets: {
          programImpact: {
            title: "Impact du programme",
            powerBititle: "Ouvrir Power BI",
            youthInWork: "Jeunes en emploi",
            youngWomenInWork: "% Jeunes femmes en emploi",
            workEnablingOutreach: "Sensibilisation favorisant l'emploi",
            womenWorkEnablingOutreach: "% Sensibilisation favorisant l'emploi des femmes",
          },
          quickAccessApps: {
            title: "Applications à accès rapide",
            description: "Vos applications les plus utilisées pour un accès rapide.",
            addApp: "Ajouter une application",
            edit: "Modifier",
            done: "Terminé",
            deleteTitle: "Supprimer l’application",
            deleteMessage: "Êtes-vous sûr de vouloir supprimer",
            deleteWarning: "Cette action est irréversible.",
            cancel: "Annuler",
            delete: "Supprimer",
            comingSoon: "Bientôt disponible",
            openInNewTab: "{{appName}} (s’ouvre dans un nouvel onglet)"
          },
          todaysPriorities: {
            loading: "Chargement des priorités…",
            error: "Échec du chargement des priorités",
            today: "Aujourd’hui",
            week: "Semaine",
          },

          programResources: {
            title: "Ressources",
            loading: "Chargement des ressources...",
            updated: "Mis à jour",
            view: "Voir",
            viewAria: "Voir {{title}}",
            items: {
              programDevelopment: "Guide de développement du programme",
              elevStrategy: "La stratégie EleV",
              safeguarding: "Lignes directrices pour la protection des enfants et des jeunes",
            },
          },
          partnerHighlights: {
            title: "Leçons apprises et faits saillants des partenaires",
            description: "Histoires et insights du terrain",
            loading: "Chargement des faits saillants des partenaires...",
            error: "Échec du chargement des faits saillants des partenaires",
            viewAll: "Voir toutes les études de cas",
          },
          financeTargets: {
            title: "Objectifs financiers",
            error: "Erreur inattendue lors du chargement des objectifs financiers",
            loading: "Aucune donnée disponible pour les objectifs financiers",
            labelFilter: "À l’échelle de la fondation :",
            filterText: "Sélectionner des plateformes",
            filterLabel: "Sélectionner des plateformes",
            powerBititle: "Ouvrir Power BI",
            headerTitle: "Indicateurs des programmes actifs",
            actual: "Décaissement du programme",
            target: "Engagement total du programme",
          },
          ideasTracker: {
            title: "Suivi des idées",
            powerBiTitle: "Ouvrir Power BI",
            openPowerBIAria: "Ouvrir le tableau de bord Power BI dans un nouvel onglet",
            metrics: "Indicateurs du suivi des idées",
            ideasSubmitted: "Idées soumises",
            underReview: "En cours d'examen",
            onHold: "En attente",
            rejected: "Rejetées",
            converted: "Converties",
          },

          PaymentsPipelineWidget: {
            title: "Pipeline de paiements",
          },
        },

        insights: {
          containerLabel: "Panneau d’analyses",
          tabsLabel: "Plage temporelle",
          tabs: {
            weekly: "Hebdomadaire",
            monthly: "Mensuel",
            quarterly: "Trimestriel",
          },
          sections: {
            timeSpent: "Temps passé sur les tâches",
            kpis: "Indicateurs de performance",
            goals: "Progression des objectifs",
          },
          viewAnalytics: "Voir les analyses détaillées",
          activeRange: "Affichage des analyses : {{range}}",
          aria: {
            toggleSection: "Basculer la section {{section}}",
          },
        },

        systemsSupport: {
          title: "Systèmes et support",
          activeRequests: "Demandes actives",
          needAssistanceTitle: "Besoin d’aide ?",
          needAssistanceBody:
            "Notre équipe d’assistance est prête à vous aider pour toute question ou problème.",
          contactSupport: "Contacter l’assistance",

          statuses: {
            pending: "En attente",
            inProgress: "En cours",
            resolved: "Résolu",
          },

          aria: {
            containerLabel: "Panneau Systèmes et support",
            listLabel: "Liste des demandes actives",
            requestItem: "Demande",
            status: "Statut",
            postedOn: "Publié le {{date}}",
            expandDetails: "Afficher les détails pour {{title}}",
            collapseDetails: "Masquer les détails pour {{title}}",
            loaded: "{{count}} demandes actives chargées",
            loading: "Chargement des demandes actives",
            error: "Échec du chargement des demandes",
            contactSupport: "Contacter l’assistance",
          },
        },

        turnaroundTimes: {
          title: "Délais de traitement",
          subtitle: "Échéances des projets",
          dueInDays: "Dû dans {{count}} jours",
          dueTomorrow: "Dû demain",
          dueToday: "Dû aujourd’hui",
          severity: {
            critical: "Critique",
            high: "Élevé",
            normal: "Normal",
          },
          aria: {
            containerLabel: "Panneau Délais de traitement",
            listLabel: "Liste des échéances",
            task: "Tâche",
            project: "Projet",
            due: "Échéance",
            severity: "Gravité",
            detailsShow: "Afficher les détails pour {{title}}",
            detailsHide: "Masquer les détails pour {{title}}",
            loading: "Chargement des échéances",
            loaded: "{{count}} échéances chargées",
            error: "Échec du chargement des échéances",
            warning: "Alerte : {{days}} jours avant retard pour le projet {{project}}",
          },
          warningTitle: "Alerte : {{days}} jours avant retard",
          warningBody: 'Le projet "{{project}}" nécessite une attention immédiate',
        },

        commUpload: {
          title: "Téléversement Communication",

          labels: {
            selectFile: "Sélectionner un fichier Excel",
            selected: "Sélectionné :",
          },

          headers: {
            tag: "Tag",
            partnerOrg: "Organisation partenaire",
            ppp1: "PPP1",
            ppp2: "PPP2 (le cas échéant)",
            fpp: "FPP",
            estQuarterOrig: "Trimestre d’approbation estimé (initial)",
          },

          buttons: {
            downloadTemplate: "Télécharger le modèle",
            downloadUploaded: "Télécharger le fichier téléversé",
            submit: "Excel vers tableau",
            submitToServer: "Soumettre au serveur",
          },

          alerts: {
            templateMissing: "Fichier modèle introuvable. HTTP {{status}}",
            templateHtmlError:
              "Le chemin du modèle renvoie du HTML au lieu d’un Excel. Vérifiez /public/templates.",
            templateDownloadFailed: "Impossible de télécharger le modèle.",
            uploadFirst: "Veuillez d’abord téléverser un fichier",
            dataSubmitted: "Données soumises avec succès !",
            dataSubmitFailed: "Échec de la soumission. {{message}}",
          },
        },

        ideasStats: {
          heading: "Statistiques des idées",
          periodLabel: "Période",
          periodQuarterly: "Trimestriel",
          tabs: ["T1", "T2", "T3", "T4"],
          total: "Total",
          inProgress: "En cours",
          progressedToConcept: "Avancé au concept",
          notProgressed: "Non avancé",
          loading: "Chargement",
          error: "Une erreur est survenue",
          announceQuarter: "Affichage de {{q}}",
          announcePeriod: "La période est passée à {{p}}",
        },
        NewIdeaHeader: {
          newIdea: {
            title: "Créer une nouvelle idée",
            subtitle: "Assigner des utilisateurs et téléverser la documentation justificative",
          },
        },
        newIdeaForm: {
          progress: "Étape {{step}} sur {{total}}",
          formLabel: "Formulaire Nouvelle Idée",
          ideaName: {
            label: "Nom de l'idée/programme",
            placeholder:
              "ex. : Programme d'alphabétisation numérique pour la jeunesse rurale en Afrique de l'Est",
          },
          characters: "caractères",
          sourceOfIdea: { label: "Source de l'idée" },
          startDate: { label: "Date de début" },
          endDate: { label: "Date de fin" },
          dateFormat: "Format : jj/mm/aaaa",
          platformThematic: { label: "Plateforme / Thématique" },
          estimatedBudget: { label: "Budget estimé (USD)", placeholder: "0" },
          optional: "Optionnel",
          programSubgroup: { label: "Sous-groupe du programme" },
          description: {
            label: "Description",
            placeholder: "Expliquez le concept du programme, les activités et l'impact prévu",
          },
          characters_min: "caractères (minimum {{min}})",
          prospectivePartner: { label: "Partenaire potentiel" },
          buttons: {
            cancel: "Annuler",
            saveDraft: "Enregistrer comme brouillon",
            continue: "Continuer vers les détails",
            back: "Retour",
            create: "Créer l'idée",
          },
          assignUser: {
            heading: "Affecter un/des utilisateur(s) à la tâche",
            label: "Utilisateur à qui affecter l'idée",
            selectUser: "Sélectionner un utilisateur",
          },
          documents: {
            heading: "Documentation supplémentaire à inclure",
            dropAreaAria: "Zone de dépôt de fichiers",
            dropText: "Glissez-déposez les fichiers ici, ou cliquez pour parcourir",
            supports: "Prend en charge le téléchargement simple ou multiple • PDF, DOC, XLS, PPT",
            browse: "Parcourir les fichiers",
            chooseFilesAria: "Choisir les fichiers à téléverser",
            remove: "Supprimer",
            removeFile: "Supprimer le fichier {{name}}",
          },
          objectives: {
            label: "Objectifs clés et indicateurs de réussite",
            placeholder: "Quels résultats spécifiques visez-vous ?",
          },
          background: {
            label: "Contexte et justification",
            placeholder: "Quel contexte, recherche ou preuve soutient cette idée ?",
          },
          risks: {
            label: "Risques clés et hypothèses",
            placeholder: "Quels défis ou dépendances devons-nous connaître ?",
          },
          tags: {
            label: "Tags",
            placeholder:
              "Ajoutez des mots-clés séparés par des virgules (ex. : Compétences numériques, Jeunesse, Rural)",
          },
        },

        /* ===========================
           Active Programs (FR)
           =========================== */
        activePrograms: {
          title: "Programmes actifs",
          loadingPhase: "Chargement de {{phase}}",
          phaseCount: "{{phase}}, {{count}} programmes",
          loadingCount: "Chargement du nombre",
          loading:"Chargement des programmes actifs",
          count: "{{count}} programmes",
          refresh:"Actualiser les programmes actifs",
          filters: {
            my: "Mes Programmes",
            team: "Mon équipe",
            all: "Tous les Programmes",
          },
          selectPhase: "Sélectionner une phase",
          actions: {
            addNew: "Ajouter un programme",
          },
          empty: {
            noData: "Aucun programme n’est actuellement disponible dans cette phase",
          },
          phases: {
            planning: "Planification et pipeline de programmes",
            ideation: "Idéation",
            concept: "Concept",
            proposal: "Élaboration de la proposition",
            review: "Révision et approbation",
            contracting: "Contractualisation",
            disbursement: "Décaissement",
            implementation: "Mise en œuvre et suivi",
            closeout: "Clôture",
          },
          aria: {
            tabsLabel: "Phases des programmes actifs",
            scrollLeft: "Faire défiler les phases vers la gauche",
            scrollRight: "Faire défiler les phases vers la droite",
          },
        },
        PipelinePriority: {
  title: "Pipeline prioritaire",
  notFound: "Aucune priorité de pipeline trouvée"
},

        keyGuidelines: {
          title: "Directives clés",
          view : "Voir",
        },

        taskPriorities: {
          title: "Priorités des tâches",

          workflow: "Du workflow",

          due: "Date limite",

          overdue: "En retard de {{count}} jours",
          buttons: {
            viewInFluxx: "Voir dans Fluxx",
          },

          states: {
            loading: "Chargement des tâches…",
            error: "Une erreur est survenue",
          },
          noData: "Aucune tâche trouvée",
          error: "Échec du chargement des priorités des tâches",
        },
        notificationTitle: "Notification de flux de travail",
        noDataNotifications: "Aucune notification disponible",
        errorNotifications: "Échec du chargement des notifications",

        notificationColumn: "Notification",
        updatedDateColumn: "Date de mise à jour",

        programMetrics: {
          title: "Indicateurs du programme",
          subtitle: "Suivez les performances de votre programme",
          viewInFluxx: "Afficher dans Fluxx",

          progressTitle: "Chronologie de la distribution du financement",
        },
        analyticsInsights: {
          title: "Analytique et Insights",
          portfolioTitle: "Performance du portefeuille",
          financialAnalytics: "Analyse",
          subtitle: "Performance du portefeuille via Power BI",
          foundationWide: "À l’échelle de la fondation",
          powerBititle: "Ouvrir Power BI",
          progressTitle: "Chronologie de la distribution des financements",
          headertitle: "Programmes",
          headerAlltitle: "Tous les programmes",
          filtertitle: "À l’échelle de la fondation",
          filterLabel: "Filtrer par plateforme",
          activePrograms: "Programmes actifs",
          activeProgramsDescription: "Programmes actuellement à un stade actif",
          approvalTurnaround: "Délai moyen d’approbation",
          approvalTurnaroundDescription:
            "Nombre moyen de jours entre la soumission et l’approbation",
          disbursementOntime: "Décaissement dans les délais",
          disbursementOntimeDescription:
            "Suit l’achèvement du processus avec un taux de décaissement complet",
          ApprovedPartners: "Partenaires approuvés",
          ApprovedPartnersDescription:
            "Organisations partenaires uniques avec des enregistrements actifs",
          avgTimeInStage: "Temps moyen par étape",
          concept: "Concept",
          proposalDevelopment: "Développement de la proposition",
          reviewApprovals: "Révision et approbations",
          contracting: "Contractualisation",
          disbursement: "Décaissement",
          Highlights: {
            title: "Résumé des tendances du pipeline",
            Subtitle:
              "Met en évidence les étapes où les programmes prennent le plus de temps à progresser · sur les 12 derniers mois",
          },
          errorMessage: "Échec du chargement des insights financiers",
          noData: "Aucune donnée disponible pour les insights financiers",
          filterText: "Sélectionner des plateformes",
        },
        learningInsights: {
          title: "Apprentissage et perspectives",
          loading: "Chargement des perspectives…",
          noInsights: "Aucune perspective disponible pour cette catégorie.",
          updated: "Mise à jour",
          readMore: "Lire la suite",
          comingSoon: "Bientôt disponible",
          tabs: {
            lessonsLearned: "Leçons apprises",
            skillBuildingTip: "Conseil pour développer vos compétences",
          },
          errors: {
            failedToLoad: "Impossible de charger les perspectives d'apprentissage",
            unexpected: "Erreur inattendue",
          },
        },
        partnerDirectory: {
          title: "Répertoire des partenaires",
          searchPlaceholder: "Rechercher un partenaire",
          country: " Sélectionner le pays",
          getDetails: "Afficher",
          noResults: "Aucun partenaire ne correspond aux critères de recherche",

          columns: {
            organization: "Détails de l’organisation",
            email: "Email de l’organisation",
            country: "Pays",
            contact: "Contact principal",
            status: "Statut",
          },

          buttons: {
            viewAll: "Voir dans Fluxx",
          },

          states: {
            noData: "Aucun partenaire trouvé",
          },
        },

        sessionTimeout: {
          title: "Avertissement de fin de session",

          description:
            "Vous êtes inactif depuis un certain temps. Pour votre sécurité, vous serez automatiquement déconnecté sauf si vous restez actif.",

          countdown: "Déconnexion automatique dans {{time}}",

          logout: "Se déconnecter maintenant",
          stay: "Rester connecté",
        },

        MeetingsWidget: {
          title: "Réunions à venir",
          noMeetings: "Aucune réunion à venir.",
          loading: "Chargement des réunions…",
          synced: "📍 Synchronisé depuis Outlook · uniquement liées aux programmes",
          schedule: "+ Planifier une réunion",
          filters: {
            all: "Toutes",
            partner: "Réunions partenaires",
            internal: "Revues internes",
            workshop: "Ateliers",
          },
          viewInOutlook: "Voir dans Outlook",
          startingSoon: "Commence bientôt",
          minsMeeting: "30 minutes",
          joinMeeting: "Rejoindre la réunion",
        },
        teamCalendarWidget: {
          title: "Calendrier d’équipe",
          loading: "Chargement du calendrier…",
          noEvents: "Aucun événement à venir",
        },
        SubmissionsWidget: {
          title: "Mes soumissions",
          activeCount: "Actives",
          description: "Suivez toutes vos notes conceptuelles, propositions et rapports",
          loading: "Chargement des soumissions...",
          empty: "Aucune soumission",
          tabs: {
            upcoming: "À venir",
            completed: "Terminées",
          },
        },

        OversightWidget: {
          loading: "Chargement des données de supervision...",
          lastSynced: "Dernière synchronisation :",
          nextSync: "Prochaine synchronisation planifiée",
          capacity: "{{value}} % de capacité",
          title: "Supervision",
          subtitle: "Système de gestion des programmes",
          status: "Ouvert",
          activePrograms: "Programmes actifs",
          pendingReviews: "Examens en attente",
          SeverelyDelayed: "Retard sévère",
          workFlowCompletion: "Taux d’achèvement du workflow",
          powerBititle: "Ouvrir Power BI",
          error: "Erreur inattendue lors du chargement des données de supervision",
          noData: "Aucune donnée disponible pour la supervision",
        },

        AnalyticsWidget: {
          name: "Analytique",
          loading: "Chargement des analyses…",
        },
        navBar: {
          sidebarItems: {
            dashboard: "Tableau de bord",
            dashboardSettings: "Paramètres du tableau de bord",
            programPlanning: "Planification et pipeline des programmes",
            ideationTracker: "Suivi des idées",
            concepts: "Concepts",
            proposals: "Propositions",
            comments: "Commentaires",
            documentServices: "Services de documents",
            learningHub: "Centre d'apprentissage",
            Menu: "le menu",
            worldClocks: "Horloges mondiales",
            accountSettings: "Paramètres du compte",
            accessibility: "Accessibilité",
            accessibilitySettings: "Paramètres d’accessibilité",
            privacyPolicy: "Politique de confidentialité",
            terms: "Conditions générales",
            safeguarding: "Protection",
          },
          header: {
            toggleMenu: "Basculer le menu",
            guest: "Invité",
            expandNavigationMenu: "Développer le menu de navigation",
            collapseNavigationMenu: "Réduire le menu de navigation",
          },
          tour: "Faire une visite guidée",
          notifications: "Notifications",
          language: "Langue",
        },

        profileMenu: {
          preferences: "Préférences",
          theme: "THÈME",
          theme1: "Thème 1 (En-tête orange)",
          theme2: "Thème 2 (En-tête clair)",
          themeDark: "Thème sombre",
          accessibility: "Accessibilité",
          signOut: "Se déconnecter",
          signIn: "Se connecter",
        },

        footer: {
          copyright: "© Fondation Mastercard 2026",
          message:
            "Ce tableau de bord est conforme aux normes WCAG 2.1 niveau AA. Pour obtenir de l’aide, veuillez contacter",
          links: {
            privacyPolicy: "Politique de confidentialité",
            accessibility: "Accessibilité",
            termsAndConditions: "Conditions générales",
            safeguarding: "Sauvegarde",
          },
          logoAlt: "Daraja",
        },
        partnerSubmission: {
          title: "Soumissions des partenaires",
          loading: "Chargement des soumissions...",
          noSubmissions: "Aucune soumission trouvée.",
          viewInFluxx: "Afficher dans Fluxx",
          dueTitle: "Dû:",
        },
        disbursementRequestsWrapper: {
          title: "Demandes de décaissement",
          subtitle: "Demandes en attente nécessitant votre examen ",
          pendingCount: "{{count}} en attente",
          totalAmount: "${{amount}} au total",
          error: "Erreur inattendue lors du chargement des demandes de décaissement",
          loading: "Chargement des demandes de décaissement...",
          reviewButton: "Examiner dans Fluxx",
          viewInFluxx: "Voir dans Fluxx",
          disbursementRequestsTitle: "Montant des demandes de décaissement",
          disbursementPaidTitle: "Montant des décaissements payés",
          noData: "Aucune demande de décaissement en attente trouvée",
        },
      },
    },
  },
};

export const initializeI18n = async () => {
  let translationResources: Resource;

  const useDbTranslations = isTranslationDbEnabled();

  if (useDbTranslations) {
    try {
      translationResources = await getTranslations();
    } catch (error) {
      console.error(
        "Failed to load translations from database. Falling back to local resources.",
        error
      );

      translationResources = resources;
    }
  } else {
    translationResources = resources;
  }

  await i18n.use(initReactI18next).init({
    resources: translationResources,
    lng: "en",
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

  return i18n;
};

export default i18n;
